/**
 * Captain Bram's character contract (/tmp/bram/CONTRACT.md, art ⇄ runtime): the node, shape-key and clip names the
 * art delivers in `art/hq/guide/captain_bram.glb`, and where the runtime finds the model and its static fallbacks.
 * Pure data: no DOM, no three, so node tests can read it.
 */

/** Bones (or plain nodes) the runtime drives or reads, exact names. */
export const BRAM_BONES = [
  "root", "spine", "chest", "neck", "head", "jaw", "eye_L", "eye_R", "brow_L", "brow_R",
  "upperarm_L", "upperarm_R", "forearm_L", "forearm_R", "hand_L", "hand_R",
] as const;
export type BramBone = (typeof BRAM_BONES)[number];

/** Shape keys on the head mesh, exact names. */
export const BRAM_MORPHS = ["blink", "mouth_open", "mouth_wide", "smile", "frown", "brows_up", "brows_angry"] as const;
export type BramMorph = (typeof BRAM_MORPHS)[number];

/**
 * Animation clips, exact names. `idle` and `talk` loop; the rest are one-shots (`point` holds its last pose).
 * `laugh` and `thumbs_up` came with Bram v2 (art/hq/guide README "Clips"); a model without them falls back
 * (bramRig.ts CLIP_FALLBACK, then bramMotion.ts).
 */
export const BRAM_CLIPS = ["idle", "talk", "point", "cheer", "wave", "nod", "think", "alarm", "salute", "laugh", "thumbs_up"] as const;
export type BramClip = (typeof BRAM_CLIPS)[number];
export type BramLoop = "idle" | "talk";

/** The contract's clip lengths (s): what the brain times one-shots by until the real clips report their own. */
export const CLIP_SECONDS: Readonly<Record<BramClip, number>> = {
  idle: 4, talk: 2, point: 0.6, cheer: 1.4, wave: 1.6, nod: 0.8, think: 2, alarm: 1.2, salute: 1.2, laugh: 1.6, thumbs_up: 1.2,
};

/** The model, relative to the web root (publicDir is art/, so art/hq/guide/… ships at hq/guide/…). */
export const BRAM_GLB = "./hq/guide/captain_bram.glb";
/** The stand-in until the real model lands: the troop soldier the old busts were rendered from. */
export const BRAM_PLACEHOLDER_GLB = "./hq/troop_soldier.glb";

/** Static art (interface root relative): the new renders, then the old busts while those are not in yet. */
export type BramArtPose = "neutral" | "pointing" | "cheering" | "wave" | "alarm" | "thumbsup";
export const bramArtPath = (pose: BramArtPose, scale: 1 | 3): string => `captain/bram_${pose}@${scale}x.png`;
/** The old three busts (captain_*.png) stand in for the new poses they are closest to. */
export const LEGACY_POSE: Readonly<Record<BramArtPose, "neutral" | "pointing" | "cheering">> = {
  neutral: "neutral", pointing: "pointing", cheering: "cheering", wave: "cheering", alarm: "pointing", thumbsup: "cheering",
};
export const legacyArtPath = (pose: BramArtPose): string => `captain/captain_${LEGACY_POSE[pose]}.png`;
/** The `@1x` renders are about this tall (the `@3x` ones are three times it). */
export const BRAM_ART_1X_PX = 300;
